import { Component, inject, ElementRef, viewChild, effect, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AiPanelService } from '../../../services/ai-panel/ai-panel.service';
import { WgAiActionCardComponent } from './wg-ai-action-card.component';

@Component({
  selector: 'app-wg-ai-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, WgAiActionCardComponent],
  encapsulation: ViewEncapsulation.None,
  styles: [`
    :host {
      display: contents;
    }
    app-wg-ai-panel,
    app-wg-ai-panel * {
      font-family: 'Inter', sans-serif;
    }
    @keyframes dot-bounce {
      0%, 80%, 100% { transform: translateY(0); }
      40% { transform: translateY(-4px); }
    }
    .dot-1 { animation: dot-bounce 1.4s ease-in-out infinite; }
    .dot-2 { animation: dot-bounce 1.4s ease-in-out 0.2s infinite; }
    .dot-3 { animation: dot-bounce 1.4s ease-in-out 0.4s infinite; }
  `],
  template: `
    <!-- Overlay for mobile -->
    @if (aiPanel.isOpen()) {
      <div
        class="fixed inset-0 bg-black/20 dark:bg-black/40 z-30 lg:hidden"
        (click)="aiPanel.toggle()">
      </div>
    }

    <!-- Toggle button (fixed, always visible) -->
    @if (!aiPanel.isOpen()) {
      <button
        (click)="aiPanel.toggle()"
        class="fixed bottom-6 right-6 z-50 size-12 rounded-full bg-primary-600 hover:bg-primary-700 text-white shadow-lg hover:shadow-xl transition-all flex items-center justify-center"
        style="z-index:99999;border:none;cursor:pointer"
        title="AI Pomočnik">
        <span class="material-symbols-outlined" style="font-size:24px">auto_awesome</span>
      </button>
    }

    <!-- Panel -->
    <div
      class="fixed top-0 right-0 h-full z-40 flex flex-col bg-white dark:bg-[#151c2b] border-l border-slate-200 dark:border-slate-800 transition-all duration-300 ease-in-out overflow-hidden shadow-2xl"
      [style.width]="aiPanel.isOpen() ? '380px' : '0px'"
      [style.min-width]="aiPanel.isOpen() ? '380px' : '0px'"
      [style.border-left-width]="aiPanel.isOpen() ? '1px' : '0px'">

      <div class="flex flex-col h-full" style="width:380px">
        <!-- Header -->
        <div class="shrink-0 px-5 py-4 border-b border-slate-200 dark:border-slate-800">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2.5">
              <span class="material-symbols-outlined" style="font-size:20px;color:#f59e0b">auto_awesome</span>
              <div>
                <h2 class="text-sm font-semibold text-slate-900 dark:text-white leading-tight">AI Pomočnik</h2>
                <p class="text-[10px] font-medium text-slate-400 dark:text-slate-500 tracking-wider uppercase">
                  @if (aiPanel.currentFormName()) {
                    {{ aiPanel.currentFormName() }}
                  } @else {
                    Collection Intelligence
                  }
                </p>
              </div>
            </div>
            <div class="flex items-center gap-1">
              <button
                (click)="aiPanel.clearChat()"
                class="size-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                title="Počisti pogovor">
                <span class="material-symbols-outlined" style="font-size:18px">delete_sweep</span>
              </button>
              <button
                (click)="aiPanel.toggle()"
                class="size-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                <span class="material-symbols-outlined" style="font-size:20px">close</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Context indicator -->
        @if (aiPanel.currentRecordId()) {
          <div class="shrink-0 px-4 py-2 bg-amber-50 dark:bg-amber-900/10 border-b border-amber-200/50 dark:border-amber-800/30">
            <div class="flex items-center gap-2 text-[11px] text-amber-700 dark:text-amber-400">
              <span class="material-symbols-outlined" style="font-size:14px">link</span>
              <span>Povezan z zapisom #{{ aiPanel.currentRecordId() }}</span>
            </div>
          </div>
        }

        <!-- Content -->
        <div class="flex-1 min-h-0 flex flex-col">
          <!-- Chat messages -->
          <div class="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-3" #chatContainer>
            @if (aiPanel.messages().length === 0 && !aiPanel.isLoading()) {
              <!-- Welcome -->
              <div class="flex flex-col items-center justify-center h-full text-center px-4">
                <div class="size-12 rounded-xl bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center mb-3">
                  <span class="material-symbols-outlined" style="font-size:24px;color:#f59e0b">auto_awesome</span>
                </div>
                <p class="text-[13px] text-slate-600 dark:text-slate-300 leading-relaxed">
                  Pozdravljeni! Sem vaš AI pomočnik. Kako vam lahko pomagam?
                </p>
                <div class="flex flex-wrap justify-center gap-2 mt-4">
                  @for (action of quickActions; track action.label) {
                    <button
                      (click)="sendQuickAction(action.message)"
                      class="px-3 py-1.5 rounded-lg text-[11px] font-medium border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                      {{ action.label }}
                    </button>
                  }
                </div>
              </div>
            } @else {
              @for (msg of aiPanel.messages(); track msg.timestamp) {
                @if (msg.role === 'user') {
                  <div class="flex justify-end">
                    <div class="max-w-[85%] px-3.5 py-2.5 rounded-2xl rounded-br-md bg-primary-600 text-white text-[13px] leading-relaxed">
                      {{ msg.content }}
                    </div>
                  </div>
                } @else {
                  <div class="flex justify-start">
                    <div class="max-w-[95%]">
                      @if (msg.content) {
                        <div class="px-3.5 py-2.5 rounded-2xl rounded-bl-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-[13px] leading-relaxed whitespace-pre-line">
                          {{ msg.content }}
                        </div>
                      }
                      @if (msg.actions && msg.actions.length > 0) {
                        @for (action of msg.actions; track action.title) {
                          <app-wg-ai-action-card [action]="action" />
                        }
                      }
                    </div>
                  </div>
                }
              }

              <!-- Typing indicator -->
              @if (aiPanel.isLoading()) {
                <div class="flex justify-start">
                  <div class="px-4 py-3 rounded-2xl rounded-bl-md bg-slate-100 dark:bg-slate-800 flex items-center gap-1">
                    <div class="size-1.5 rounded-full bg-slate-400 dark:bg-slate-500 dot-1"></div>
                    <div class="size-1.5 rounded-full bg-slate-400 dark:bg-slate-500 dot-2"></div>
                    <div class="size-1.5 rounded-full bg-slate-400 dark:bg-slate-500 dot-3"></div>
                  </div>
                </div>
              }

              <!-- Quick actions after last assistant message -->
              @if (!aiPanel.isLoading() && lastMessageIsAssistant()) {
                <div class="flex flex-wrap gap-1.5 pt-1">
                  @for (action of quickActions; track action.label) {
                    <button
                      (click)="sendQuickAction(action.message)"
                      class="px-2.5 py-1 rounded-md text-[11px] font-medium border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                      {{ action.label }}
                    </button>
                  }
                </div>
              }
            }
          </div>

          <!-- Input -->
          <div class="shrink-0 border-t border-slate-200 dark:border-slate-800 p-3">
            <div class="flex items-center gap-2">
              <input
                type="text"
                [(ngModel)]="inputText"
                (keydown.enter)="send()"
                placeholder="Vprašaj kaj o tem zapisu..."
                class="flex-1 px-3.5 py-2 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 text-[13px] text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 outline-none focus:border-primary-400 dark:focus:border-primary-500 transition-colors"
              />
              <button
                (click)="send()"
                [disabled]="!inputText.trim() || aiPanel.isLoading()"
                class="size-9 rounded-lg flex items-center justify-center bg-primary-600 hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed text-white transition-colors shrink-0">
                <span class="material-symbols-outlined" style="font-size:18px">send</span>
              </button>
            </div>
            <p class="text-[10px] text-slate-400 dark:text-slate-500 mt-2 text-center">
              AI lahko naredi napake. Preverite pomembne podatke.
            </p>
          </div>
        </div>
      </div>
    </div>
  `
})
export class WgAiPanelComponent {
  aiPanel = inject(AiPanelService);
  chatContainer = viewChild<ElementRef>('chatContainer');

  inputText = '';

  quickActions = [
    { label: 'Opiši predmet', message: 'Opiši ta predmet' },
    { label: 'Predlagaj metapodatke', message: 'Predlagaj metapodatke za ta zapis' },
    { label: 'Prevedi', message: 'Prevedi opis v angleščino' },
  ];

  constructor() {
    effect(() => {
      this.aiPanel.messages();
      this.aiPanel.isLoading();
      setTimeout(() => this.scrollToBottom(), 50);
    });
  }

  lastMessageIsAssistant(): boolean {
    const msgs = this.aiPanel.messages();
    return msgs.length > 0 && msgs[msgs.length - 1].role === 'assistant';
  }

  send() {
    const text = this.inputText.trim();
    if (!text || this.aiPanel.isLoading()) return;
    this.inputText = '';
    this.aiPanel.sendMessage(text);
  }

  sendQuickAction(message: string) {
    if (this.aiPanel.isLoading()) return;
    this.aiPanel.sendMessage(message);
  }

  private scrollToBottom() {
    const el = this.chatContainer()?.nativeElement;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }
}
